import React from 'react';
import { styled } from '@mui/material';

import ProjectsOverview from 'components/organisms/CourseDashboard/ProjectsOverview';
import { Box, Button, Divider, Icon, Stack, Text } from 'shell/components';
import { useSnackbar } from 'contexts/snackbar';

const Root = styled(Stack)(({ device, theme }) => {
    return {
        paddingTop: theme.spacing(4),
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
    };
});

const CourseInfoBox = styled(Box)(({ device, theme }) => {
    return {
        display: 'grid',
        alignItems: 'center',
        gridTemplateColumns: '100px 1fr',
        gridRowGap: theme.spacing(2),
        marginTop: theme.spacing(4),
        ...(device === 'desktop' && {
            gridTemplateColumns: '100px 1fr 100px 1fr',
        }),
    };
});

const CourseOverviewTab = ({
    device,
    course,
    role,
    project,
    numGroups,
    selectProject,
    setProjectSettings,
    handleProjectCardDropdown,
    handleGenerateId,
}) => {
    const { message } = useSnackbar();

    const copyGeneratedIdToClipboard = () => {
        navigator.clipboard.writeText(course.generated_id).then(
            () => message.success('Course ID copied.'),
            (error) => console.error(error)
        );
    };

    const renderCourseID = () => {
        return (
            <>
                <Text variant={'p2'} weight={'semibold'}>
                    Course ID
                </Text>
                {course?.generated_id ? (
                    <Stack sx={{ width: 'fit-content' }}>
                        <Button
                            color={'neutral'}
                            endIcon={<Icon kind={'copy'} />}
                            tooltip={{ component: 'Copy Course ID' }}
                            variant={'borderless'}
                            onClick={copyGeneratedIdToClipboard}
                        >
                            {course?.generated_id}
                        </Button>
                    </Stack>
                ) : (
                    <Button sx={{ width: 'fit-content' }} variant={'borderless'} onClick={handleGenerateId}>
                        Generate ID
                    </Button>
                )}
            </>
        );
    };

    const renderCourseInfo = () => {
        if (device !== 'desktop') {
            return (
                <>
                    {role === 'instructor' && renderCourseID()}
                    <Text variant={'p2'} weight={'semibold'}>
                        Role
                    </Text>
                    <Text variant={'p2'}>{role.charAt(0).toUpperCase() + role.slice(1)}</Text>
                    <Text variant={'p2'} weight={'semibold'}>
                        School
                    </Text>
                    <Text variant={'p2'}>{course?.school}</Text>
                    <Text variant={'p2'} weight={'semibold'}>
                        Faculty
                    </Text>
                    <Text variant={'p2'}>{course?.faculty}</Text>
                    <Text variant={'p2'} weight={'semibold'}>
                        Section
                    </Text>
                    <Text variant={'p2'}>{course?.section}</Text>
                    <Text variant={'p2'} weight={'semibold'}>
                        Semester
                    </Text>
                    <Text variant={'p2'}>{course?.semester}</Text>
                </>
            );
        }

        return role === 'instructor' ? (
            <>
                {renderCourseID()}
                <Text variant={'p2'} weight={'semibold'}>
                    Role
                </Text>
                <Text variant={'p2'}>{role.charAt(0).toUpperCase() + role.slice(1)}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    School
                </Text>
                <Text variant={'p2'}>{course?.school}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Section
                </Text>
                <Text variant={'p2'}>{course?.section}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Semester
                </Text>
                <Text variant={'p2'}>{course?.semester}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Faculty
                </Text>
                <Text variant={'p2'}>{course?.faculty}</Text>
            </>
        ) : (
            <>
                <Text variant={'p2'} weight={'semibold'}>
                    Role
                </Text>
                <Text variant={'p2'}>{role.charAt(0).toUpperCase() + role.slice(1)}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Section
                </Text>
                <Text variant={'p2'}>{course?.section}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    School
                </Text>
                <Text variant={'p2'}>{course?.school}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Semester
                </Text>
                <Text variant={'p2'}>{course?.semester}</Text>
                <Text variant={'p2'} weight={'semibold'}>
                    Faculty
                </Text>
                <Text variant={'p2'}>{course?.faculty}</Text>
            </>
        );
    };

    return (
        <Root device={device} divider={<Divider />} spacing={4}>
            <Stack>
                <Text variant={'h6'} weight={'semibold'}>
                    {course?.name}
                </Text>
                <CourseInfoBox device={device}>{renderCourseInfo()}</CourseInfoBox>
            </Stack>
            <ProjectsOverview
                device={device}
                role={role}
                project={project}
                numGroups={numGroups}
                selectProject={selectProject}
                setProjectSettings={setProjectSettings}
                handleProjectCardDropdown={handleProjectCardDropdown}
            />
        </Root>
    );
};

export default CourseOverviewTab;
